import type { BrandKitLayoutSource } from '@/lib/layouts/types';

export interface HarvestableKitData {
  colors?: Array<string | null> | null;
  logo?: { logo_image_url?: string | null } | null;
  post_layout?: {
    kind?: string | null;
    config?: Record<string, unknown> | null;
  } | Record<string, unknown> | null;
}

function nonEmptyString(value: unknown): string | undefined {
  if (typeof value === 'string' && value.trim() !== '') {
    return value;
  }
  return undefined;
}

function resolveConfig(
  postLayout: HarvestableKitData['post_layout'],
): Record<string, unknown> | undefined {
  if (!postLayout || typeof postLayout !== 'object') {
    return undefined;
  }

  if ('config' in postLayout && postLayout.config && typeof postLayout.config === 'object') {
    return postLayout.config as Record<string, unknown>;
  }

  const rest = { ...(postLayout as Record<string, unknown>) };
  delete rest.kind;
  delete rest.config;
  return Object.keys(rest).length > 0 ? rest : undefined;
}

function firstDisplayName(config: Record<string, unknown> | undefined): string | undefined {
  if (!config) return undefined;
  return (
    nonEmptyString(config.displayName)
    ?? nonEmptyString(config.profileName)
    ?? nonEmptyString(config.brandName)
    ?? nonEmptyString(config.channelName)
    ?? nonEmptyString(config.sourceName)
  );
}

export function harvestBrandKitLayoutSource(
  data: HarvestableKitData | null | undefined,
): BrandKitLayoutSource | null {
  if (data == null) {
    return null;
  }

  const config = resolveConfig(data.post_layout);
  const logoFallback = nonEmptyString(data.logo?.logo_image_url);

  return {
    logoUrl: nonEmptyString(config?.logoUrl) ?? logoFallback,
    avatarUrl: nonEmptyString(config?.avatarUrl) ?? logoFallback,
    accentColor:
      nonEmptyString(config?.accentColor)
      ?? nonEmptyString(config?.titleAccentColor)
      ?? nonEmptyString(data.colors?.[0]),
    handle: nonEmptyString(config?.handle),
    displayName: firstDisplayName(config),
  };
}
